var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { DebounceManager } from '@inter-ikea-kompis/utilities';
import SheetAlignmentEnum from '../../enums/SheetAlignmentEnum.js';
import SheetAnimationNameEnum from '../../enums/SheetAnimationNameEnum.js';
import SheetSizeEnum from '../../enums/SheetSizeEnum.js';
import SheetBackdrop from '../sheet-backdrop/SheetBackdrop.js';
import SheetHeader from '../sheet-header/SheetHeader.js';
import SheetStyle from './SheetStyle.js';
/**
 * @deprecated This component has been deprecated, see Kompis page for more info.
 * Sheet component.
 *
 * @event overlayClick When the overlay or escape is clicked.
 * @event animationEnd When the animation ends.
 */
export default class Sheet extends KompisElement {
    constructor() {
        super(...arguments);
        // Hide or show sheet with transitions.
        this.visible = false;
        // Override z-index of the Sheet.
        this.zIndex = 500;
        // Preserve alignment in mobile only. Ignores mobile layout and uses side sheet.
        this.preserveAlignment = false;
        // Sheet alignment.
        this.sheetAlignment = SheetAlignmentEnum.right;
        // Sheet size.
        this.sheetSize = SheetSizeEnum.small;
        // Label for the sheet to be used when a heading is not provided in sheetHeader.
        this.ariaLabel = null;
        this.isAnimating = false;
        this.debounce = new DebounceManager();
        /**
         * Closes sheet on escape.
         *
         * @param event Event.
         */
        this.onKeyDownListener = (event) => {
            if (event.code === 'Escape' && this.visible) {
                this.dispatchEvent(new CustomEvent('overlayClick', {
                    bubbles: true,
                    detail: {
                        type: 'overlay'
                    }
                }));
            }
        };
    }
    /**
     * Renders the component.
     *
     * @returns Template.
     */
    render() {
        if (!this.visible && !this.isAnimating) {
            return null;
        }
        return html `
			<style>
				${SheetStyle.getStyle({
            theme: this.theme,
            zIndex: this.zIndex,
            sheetAlignment: this.sheetAlignment,
            preserveAlignment: this.preserveAlignment,
            sheetSize: this.sheetSize,
            visible: this.visible,
            direction: window.getComputedStyle(this).direction,
            isAnimating: this.isAnimating
        })}
			</style>
			${this.getBackdrop()}${this.getContainer()}
		`;
    }
    connectedCallback() {
        this.customElements.define('kompis-sheet-backdrop', SheetBackdrop);
        super.connectedCallback();
        window.addEventListener('keydown', this.onKeyDownListener);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.isAnimating = false;
        window.removeEventListener('keydown', this.onKeyDownListener);
        this.debounce.clear('visibleSheet');
    }
    focus() {
        this.shadowRoot?.querySelector('.sheet')?.focus();
    }
    /**
     * @param changedProperties Changed properties.
     * @returns "true" if it should update.
     */
    shouldUpdate(changedProperties) {
        if (changedProperties.has('visible')) {
            this.isAnimating = true;
        }
        return true;
    }
    /**
     * @returns Template.
     */
    getBackdrop() {
        return html `
			<kompis-sheet-backdrop
				@click="${this.handleOverlayClick.bind(this)}"
				.visible="${this.visible}"
				.theme="${this.theme}"
			></kompis-sheet-backdrop>
		`;
    }
    /**
     * @returns - Html.
     */
    getContainer() {
        const header = (Array.from(this.children).find((element) => element instanceof SheetHeader));
        return html `
			<div class="container">
				<div
					class=""
					tabindex="0"
					@focusin="${() => {
            this.focus();
        }}"
				></div>
				<div
					class="sheet"
					role="dialog"
					aria-modal="${this.visible ? 'true' : 'false'}"
					aria-label="${this.ariaLabel || header?.heading || ''}"
					@animationend="${this.onAnimationEnd.bind(this)}"
					tabindex="-1"
				>
					<slot></slot>
				</div>
				<div
					class="space"
					tabindex="0"
					@focusin="${() => {
            this.focus();
        }}"
				></div>
			</div>
		`;
    }
    /**
     * Listener for animationend.
     *
     * @param event Html event.
     */
    onAnimationEnd(event) {
        this.isAnimating = false;
        if (event.animationName.startsWith(SheetAnimationNameEnum.closing)) {
            this.dispatchEvent(new CustomEvent('animationEnd', {
                bubbles: true,
                detail: {
                    type: 'close'
                }
            }));
        }
        else if (event.animationName.startsWith(SheetAnimationNameEnum.opening)) {
            this.dispatchEvent(new CustomEvent('animationEnd', {
                bubbles: true,
                detail: {
                    type: 'open'
                }
            }));
            if (this.visible) {
                this.debounce.set('visibleSheet', () => {
                    this.focus();
                }, 1);
            }
        }
        this.update();
    }
    /**
     * Dispatch close event.
     */
    handleOverlayClick() {
        if (this.isAnimating) {
            return;
        }
        this.dispatchEvent(new CustomEvent('overlayClick', {
            bubbles: true,
            detail: {
                type: 'overlay'
            }
        }));
    }
}
__decorate([
    property({ type: Boolean })
], Sheet.prototype, "visible", void 0);
__decorate([
    property({ type: Object, required: true })
], Sheet.prototype, "theme", void 0);
__decorate([
    property({ type: Number })
], Sheet.prototype, "zIndex", void 0);
__decorate([
    property({ type: Boolean })
], Sheet.prototype, "preserveAlignment", void 0);
__decorate([
    property({ type: String })
], Sheet.prototype, "sheetAlignment", void 0);
__decorate([
    property({ type: String })
], Sheet.prototype, "sheetSize", void 0);
__decorate([
    property({ type: String })
], Sheet.prototype, "ariaLabel", void 0);
//# sourceMappingURL=Sheet.js.map